import './applySearchModelPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { RadioGroupLoading } from '../../../components/RadioGroupLoading/RadioGroupLoading';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { useDelayedLoading } from '../../../store/apiStore/hooks/useDelayedLoading';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const {
    model,
    models,
    fetchVehicleModels,
    fetchVehicleModelsFailedMessage,
    isFetchingVehicleModels,
    setModelByValue,
  } = useApplyStore();

  const { register, handleSubmit, watch } = useForm({
    defaultValues: {
      modelValue: get(model, 'value'),
    },
  });

  const isLoading = useDelayedLoading(isFetchingVehicleModels);

  useEffect((): void => {
    fetchVehicleModels();
  }, []);

  const formSubmit = (values: FieldValues): void => {
    const { modelValue } = values;

    if (modelValue) {
      setModelByValue(modelValue);

      navigate(next());
    }
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (
      !('target' in event && 'value' in event.target) ||
      !isValidClickEvent(event)
    ) {
      return;
    }

    formSubmit({ modelValue: event.target.value });
  };

  return (
    <div className="apply-search-model-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header
            progress={useWorkflowStore.getState().computed.progress}
            showDrawerToggle
            onBackClick={previousStep}
          />

          <main>
            <form
              data-testid={locator.pages.applySearchModel.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applySearchModel.form.h1}>
                  {t('ApplySearchModelPage.title')}
                </h1>
                <h2 data-testid={locator.pages.applySearchModel.form.h2}>
                  {t('ApplySearchModelPage.subtitle')}
                </h2>

                {!isLoading && !isEmpty(fetchVehicleModelsFailedMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={locator.pages.applySearchModel.alert.error}
                      icon={false}
                      severity="error"
                    >
                      {fetchVehicleModelsFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  {isLoading && (
                    <RadioGroupLoading
                      loadingCount={10}
                      variant="stacked-compact"
                    />
                  )}

                  {!isLoading && (
                    <RadioGroup
                      name="modelValue"
                      options={models.map((modelObj, index) => ({
                        label: modelObj.label,
                        value: modelObj.value,
                        testId:
                          locator.pages.applySearchModel.form.getRadioOption(
                            index,
                          ),
                      }))}
                      register={register}
                      testId={
                        locator.pages.applySearchModel.form.radioGroupRoot
                      }
                      variant="stacked-compact"
                      watch={watch}
                      onClick={radioOptionClicked}
                    />
                  )}
                </div>

                {/* allows form submission using the keyboard without a visible button*/}
                <button
                  aria-hidden="true"
                  data-testid={
                    locator.pages.applySearchModel.form.accessibilitySubmit
                  }
                  style={{ display: 'none' }}
                  type="submit"
                />
              </div>
              {!isLoading && (
                <footer
                  data-testid={locator.pages.applySearchModel.footer.root}
                >
                  {t('copyright', { year: new Date().getFullYear() })}
                </footer>
              )}
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplySearchModelPage';
